import { useEffect, useState, useCallback } from "react";

export default function BackToTop() {
  const [show, setShow] = useState(false);
  const [progress, setProgress] = useState(0);

  // Show/hide button + calculate scroll progress
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const scrollHeight = document.body.scrollHeight - window.innerHeight;
      const scrolled = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;

      setShow(scrollTop > 400);
      setProgress(scrolled);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTop = useCallback(() => {
    const el = document.getElementById("hero");
    const top = el ? el.offsetTop : 0;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  // Circle metrics
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <button
      type="button"
      onClick={goTop}
      aria-label="Go to top"
      className={[
        "fixed right-4 bottom-4 z-50 transition-all duration-300 group",
        show
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none",
        "active:scale-95",
      ].join(" ")}
    >
      <span className="relative grid h-14 w-14 place-items-center rounded-full bg-[color:var(--color-bg-dark)]/40 backdrop-blur-sm border border-white/10 animate-float-slow">
        {/* scroll progress ring */}
        <svg
          className="absolute inset-0 -rotate-90"
          viewBox="0 0 64 64"
          width="64"
          height="64"
        >
          <circle
            cx="32"
            cy="32"
            r={radius}
            stroke="rgba(255,255,255,0.15)"
            strokeWidth="4"
            fill="transparent"
          />
          <circle
            cx="32"
            cy="32"
            r={radius}
            stroke="url(#progressGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            fill="transparent"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
              transition: "stroke-dashoffset 0.2s linear",
            }}
          />
          <defs>
            <linearGradient id="progressGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="rgba(0,245,160,.9)" />
              <stop offset="50%" stopColor="rgba(255,0,127,.9)" />
              <stop offset="100%" stopColor="rgba(140,48,255,.9)" />
            </linearGradient>
          </defs>
        </svg>

        {/* neon ring background (spins slightly on hover) */}
        <span className="absolute -inset-[3px] -z-10 rounded-full bg-[conic-gradient(from_0deg,rgba(0,245,160,.8),rgba(255,0,127,.8),rgba(140,48,255,.8),rgba(0,245,160,.8))] blur-md opacity-80 transition-transform duration-700 ease-out will-change-transform group-hover:rotate-180" />

        {/* arrow */}
        <svg
          className="h-5 w-5 text-[--color-primary] transition-transform duration-300 group-hover:-translate-y-[2px]"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 19V5" />
          <path d="M18 11l-6-6-6 6" />
        </svg>
      </span>
    </button>
  );
}